// Data Collection Service
// Tracks question-level events and user sessions for analytics.
// High-value data for education, insurance, and AI companies.
//
// Uses in-memory buffer for question events — flushed on test completion
// (not per-question) to minimize DB calls.

import { createClient } from '@supabase/supabase-js';
import { logger } from '../utils/logger';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseKey = process.env.REACT_APP_SUPABASE_ANON_KEY;
if (!supabaseUrl || !supabaseKey) {
  throw new Error('Supabase env vars not configured. Set REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY.');
}

const supabase = createClient(supabaseUrl, supabaseKey);

// ─── Types ───────────────────────────────────────

export interface QuestionEvent {
  user_id: string;
  test_id: string;
  question_id: string;
  topic_id: string;
  selected_answer_id: string | null;
  correct_answer_id: string;
  is_correct: boolean;
  time_spent_ms: number;
  language: string;
}

// ─── In-memory buffer ────────────────────────────

let questionEventBuffer: QuestionEvent[] = [];

// ─── Question Events ─────────────────────────────

/**
 * Adds a question event to the in-memory buffer.
 * Call this in handleAnswer() — no DB writes happen here.
 */
export function bufferQuestionEvent(event: QuestionEvent): void {
  questionEventBuffer.push(event);
}

/**
 * Flushes all buffered question events to Supabase.
 * Call this once in handlePracticeComplete() — single batch write.
 */
export async function flushQuestionEvents(): Promise<boolean> {
  if (questionEventBuffer.length === 0) return true;

  try {
    const batch = [...questionEventBuffer];
    questionEventBuffer = []; // Clear buffer before writing (prevent double-flush)

    const { error } = await supabase
      .from('question_events')
      .insert(batch);

    if (error) {
      logger.error('flushQuestionEvents error:', error);
      // Put events back in buffer for retry
      questionEventBuffer = [...batch, ...questionEventBuffer];
      return false;
    }

    logger.info(`Flushed ${batch.length} question events to Supabase`);
    return true;
  } catch (err) {
    logger.error('flushQuestionEvents exception:', err);
    return false;
  }
}

/**
 * Clears the buffer without writing (e.g., on test cancel/reset).
 */
export function clearQuestionEventBuffer(): void {
  questionEventBuffer = [];
}

/**
 * Returns current buffer size (for debugging/UI).
 */
export function getBufferSize(): number {
  return questionEventBuffer.length;
}

// ─── Session Tracking ────────────────────────────

let currentSessionId: string | null = null;

/**
 * Starts a new user session. Returns the session UUID.
 * Call on app mount (AdvancedLayout).
 */
export async function startSession(
  userId: string,
  language: string = 'en'
): Promise<string | null> {
  try {
    // Detect device type
    const width = window.innerWidth;
    let deviceType = 'desktop';
    if (width <= 480) deviceType = 'mobile';
    else if (width <= 768) deviceType = 'tablet';

    const { data, error } = await supabase
      .from('user_sessions')
      .insert({
        user_id: userId,
        language,
        device_type: deviceType,
        session_start: new Date().toISOString()
      })
      .select('id')
      .single();

    if (error || !data) {
      logger.error('startSession error:', error);
      return null;
    }

    currentSessionId = data.id;
    logger.info(`Session started: ${currentSessionId}`);
    return currentSessionId;
  } catch (err) {
    logger.error('startSession exception:', err);
    return null;
  }
}

/**
 * Ends the current session with final counts.
 * Call on unmount/beforeunload (AdvancedLayout).
 */
export async function endSession(
  questionsAttempted: number = 0,
  testsCompleted: number = 0
): Promise<boolean> {
  if (!currentSessionId) return false;

  try {
    const sessionEnd = new Date();

    // Get session start to compute duration
    const { data: session } = await supabase
      .from('user_sessions')
      .select('session_start')
      .eq('id', currentSessionId)
      .single();

    let durationSeconds = 0;
    if (session?.session_start) {
      const start = new Date(session.session_start);
      durationSeconds = Math.floor((sessionEnd.getTime() - start.getTime()) / 1000);
    }

    const { error } = await supabase
      .from('user_sessions')
      .update({
        session_end: sessionEnd.toISOString(),
        duration_seconds: durationSeconds,
        questions_attempted: questionsAttempted,
        tests_completed: testsCompleted
      })
      .eq('id', currentSessionId);

    if (error) {
      logger.error('endSession error:', error);
      return false;
    }

    logger.info(`Session ended: ${currentSessionId} (${durationSeconds}s, ${questionsAttempted} questions, ${testsCompleted} tests)`);
    currentSessionId = null;
    return true;
  } catch (err) {
    logger.error('endSession exception:', err);
    return false;
  }
}

/**
 * Returns the current session ID (or null if no active session).
 */
export function getCurrentSessionId(): string | null {
  return currentSessionId;
}

/**
 * Updates session counters without ending the session.
 * Call after each test completion.
 */
export async function updateSessionCounters(
  questionsAttempted: number,
  testsCompleted: number
): Promise<void> {
  if (!currentSessionId) return;

  try {
    await supabase
      .from('user_sessions')
      .update({
        questions_attempted: questionsAttempted,
        tests_completed: testsCompleted
      })
      .eq('id', currentSessionId);
  } catch (err) {
    logger.error('updateSessionCounters error:', err);
  }
}
